import React, { useEffect, useRef, useState } from "react";
import usePagination from "../../tailwindUI/usePagination";
import Table from "../../tailwindUI/Table";
import Badge from "../../tailwindUI/Badge";
import useFormatDate from "../useFormatDate";
import useFormatPhoneNumber from "../useFormatPhoneNumber";
import { getNotifications } from "../../apiClient/operations/paymentCollectionManagerOperations";

const tableUserNotificationColumns = [
  { heading: "Fecha", value: "date" },
  { heading: "Estatus", value: "status" },
];

const emptySummary = [
  { date: "No disponible", pending: 0, sent: 0, fail: 0, canceled: 0 },
];

const LIMIT = 10;

export function useNotifications({ campaignId, refresh }) {
  const [summaryData, setSummaryData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const [search, setSearch] = useState("");
  const paginate = usePagination();
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { formatDate } = useFormatDate();
  const { formatPhoneNumber } = useFormatPhoneNumber();

  const getNotificationsData = async (page) => {
    try {
      if (!campaignId) return;
      setIsLoading(true);
      const res = await getNotifications({
        campaignId,
        search,
        limit: LIMIT,
        page: page ? page : currentPage,
      });

      setPagination(
        paginate(res.total_pages, LIMIT, Math.ceil(res.total_items / LIMIT))
      );

      if (!res?.data || res?.data?.length === 0) {
        setSummaryData(emptySummary);
        setUsersData(null);
        return;
      }

      const summaryData = res?.campaign_detail?.notifications?.map(
        (notification) => ({
          date: formatDate(notification?.date, "DD MMMM YYYY, h:mm a"),
          ...transformSummary(notification?.summary || []),
        })
      );

      setSummaryData(summaryData);

      const usersData = res?.data?.map((user) => ({
        email: user?.email,
        phone_number: formatPhoneNumber(user?.phone_number),
        channel: user.channel,
        content: (
          <div className="w-full bg-gray-50 px-4 md:px-6 lg:px-12 py-8 space-y-8">
            <Table
              data={user?.notifications?.map((notification) => ({
                date: formatDate(notification?.date, "DD MMMM YYYY, h:mm a"),
                status: <Badge text={notification?.status} />,
              }))}
              columns={tableUserNotificationColumns}
            />
          </div>
        ),
      }));
      setUsersData(usersData);
    } catch (error) {
      handleScroll();
      setError(error?.message || "Error al procesar tu solicitud");
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = () => {
    scrollRef.current?.scrollTo({
      top: 0,
      behavior: "auto",
    });
  };

  const transformSummary = (summary) => {
    const newSummary = {
      pending: 0,
      sent: 0,
      fail: 0,
      canceled: 0,
    };
    for (const item of summary) {
      newSummary[item.status] = item.total_registers;
    }
    return newSummary;
  };

  useEffect(() => {
    getNotificationsData();
  }, [currentPage]);

  useEffect(() => {
    getNotificationsData(1);
  }, [search]);

  useEffect(() => {
    if (refresh) {
      getNotificationsData(currentPage);
    }
  }, [refresh]);

  return {
    summaryData,
    usersData,
    pagination,
    currentPage,
    setCurrentPage,
    isLoading,
    error,
    setError,
    scrollRef,
    search,
    setSearch,
  };
}
